import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Flame,
  Plus,
  Package,
  Leaf,
  TrendingUp,
  CheckCircle,
  Clock,
  AlertCircle,
  Play,
  BarChart3
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from 'recharts';
import { format } from 'date-fns';
import api, { getBiocharBatches, getBiocharSummary } from '../../services/api';
import clsx from 'clsx';
import PageHeader from '../../components/PageHeader';
import StatCard from '../../components/StatCard';
import { CustomTooltip, gridProps, axisTick, barRadius, chartMargin } from '../../components/ChartConfig';

const statusConfig = {
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-800', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  verified: { label: 'Verified', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
};

// Pipeline stage config for lantana workflow
const pipelineStages = [
  { key: 'Identified', status: 'identified', color: '#ef4444', icon: AlertCircle },
  { key: 'Clearing', status: 'clearing_in_progress', color: '#f59e0b', icon: Play },
  { key: 'Cleared', status: 'cleared', color: '#22c55e', icon: CheckCircle },
  { key: 'Verified', status: 'verified', color: '#3b82f6', icon: CheckCircle },
];

export default function BiocharDashboard({ ranchId }) {
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['biocharSummary', ranchId],
    queryFn: () => getBiocharSummary(ranchId),
  });

  const { data: recentBatches } = useQuery({
    queryKey: ['biocharBatches', ranchId, '', 1],
    queryFn: () => getBiocharBatches(ranchId, { page_size: 5 }),
  });

  const { data: lantanaPatches } = useQuery({
    queryKey: ['lantanaPatches', ranchId],
    queryFn: async () => {
      try {
        const res = await api.get('/paddocks/lantana', { params: { ranch_id: ranchId } });
        return res.data;
      } catch { return []; }
    },
  });

  // Lantana pipeline data
  const patchCounts = {};
  if (lantanaPatches?.length) {
    for (const p of lantanaPatches) {
      patchCounts[p.status] = (patchCounts[p.status] || 0) + 1;
    }
  }
  const totalPatches = lantanaPatches?.length || 0;

  // Build production chart from real batch data
  const productionData = (() => {
    if (!recentBatches?.items?.length) return [];
    const byMonth = {};
    for (const batch of recentBatches.items) {
      const month = format(new Date(batch.production_date), 'MMM yyyy');
      if (!byMonth[month]) byMonth[month] = { month: format(new Date(batch.production_date), 'MMM'), produced: 0, applied: 0 };
      byMonth[month].produced += batch.char_output_kg || 0;
    }
    return Object.values(byMonth);
  })();

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Biochar Operations"
        subtitle="Lantana clearing and biochar production"
        icon={Flame}
        actions={
          <>
            <Link to="/biochar/batches" className="btn bg-earth-100 text-earth-700 hover:bg-earth-200">
              <Package className="h-4 w-4 mr-2" />
              All Batches
            </Link>
            <Link to="/biochar/new" className="btn btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              New Batch
            </Link>
          </>
        }
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Batches"
          value={summary?.total_batches || 0}
          icon={Package}
          color="amber"
        />
        <StatCard
          title="kg Produced"
          value={(summary?.total_produced_kg || 0).toLocaleString()}
          icon={TrendingUp}
          color="green"
        />
        <StatCard
          title="Patches Found"
          value={summary?.patches_identified || 0}
          icon={Leaf}
          color="red"
        />
        <StatCard
          title="Patches Cleared"
          value={summary?.patches_cleared || 0}
          icon={Flame}
          color="primary"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Production Chart */}
        <div className="section-card">
          <div className="section-card-header">
            <h3 className="section-card-title">Production</h3>
            <BarChart3 className="h-5 w-5 text-earth-400" />
          </div>
          <div className="section-card-body">
            {productionData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={productionData} margin={chartMargin}>
                    <defs>
                      <linearGradient id="prodGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2d4a3e" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#2d4a3e" stopOpacity={0.6} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid {...gridProps} />
                    <XAxis dataKey="month" tick={axisTick} />
                    <YAxis tickFormatter={(v) => `${v}kg`} tick={axisTick} />
                    <Tooltip content={<CustomTooltip formatter={(v) => `${v} kg`} />} />
                    <Legend />
                    <Bar dataKey="produced" name="Char Output" fill="url(#prodGrad)" radius={barRadius} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-earth-500">
                <div className="text-center">
                  <Flame className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p className="font-medium">No production data yet</p>
                  <p className="text-sm mt-1">Create your first biochar batch to see production trends</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Lantana Clearing Pipeline */}
        <div className="section-card">
          <div className="section-card-header">
            <h3 className="section-card-title">Lantana Clearing Pipeline</h3>
            <Link to="/lantana" className="text-sm text-primary-600 hover:text-primary-800">Manage</Link>
          </div>
          <div className="section-card-body">
            {totalPatches > 0 ? (
              <div className="space-y-4">
                {pipelineStages.map((stage) => {
                  const count = patchCounts[stage.status] || 0;
                  const pct = totalPatches > 0 ? Math.round((count / totalPatches) * 100) : 0;
                  const StageIcon = stage.icon;
                  return (
                    <div key={stage.key} className="flex items-center gap-3">
                      <StageIcon className="h-4 w-4 shrink-0" style={{ color: stage.color }} />
                      <span className="w-20 text-sm text-earth-600">{stage.key}</span>
                      <div className="flex-1 h-6 bg-earth-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full flex items-center justify-end pr-2.5 transition-all duration-500"
                          style={{
                            width: `${Math.max(pct, count > 0 ? 12 : 0)}%`,
                            backgroundColor: stage.color,
                          }}
                        >
                          {count > 0 && (
                            <span className="text-xs font-bold text-white drop-shadow-sm">{count}</span>
                          )}
                        </div>
                      </div>
                      <span className="w-10 text-xs font-medium text-earth-500 text-right">{pct}%</span>
                    </div>
                  );
                })}
                <div className="pt-3 border-t border-earth-100 flex items-center justify-between text-sm">
                  <span className="text-earth-500">Total patches</span>
                  <span className="font-semibold text-gray-900">{totalPatches}</span>
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-earth-500">
                <div className="text-center">
                  <Leaf className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p className="font-medium">No lantana patches recorded</p>
                  <Link to="/lantana/new" className="text-primary-600 text-sm hover:underline mt-1 inline-block">
                    Add first patch
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Batches */}
      <div className="section-card">
        <div className="section-card-header">
          <h3 className="section-card-title">Recent Batches</h3>
          <Link to="/biochar/batches" className="text-sm text-primary-600 hover:text-primary-800">View All</Link>
        </div>
        <div className="section-card-body">
          {recentBatches?.items?.length > 0 ? (
            <div className="space-y-2">
              {recentBatches.items.slice(0, 5).map((batch) => {
                const config = statusConfig[batch.status] || statusConfig.in_progress;
                return (
                  <Link
                    key={batch.id}
                    to={`/biochar/batches/${batch.id}`}
                    className="flex items-center justify-between p-3 bg-earth-50 rounded-lg hover:bg-earth-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary-50 rounded-xl ring-1 ring-primary-100">
                        <Flame className="h-5 w-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{batch.batch_code}</p>
                        <p className="text-sm text-earth-500">
                          {format(new Date(batch.production_date), 'MMM d, yyyy')} &middot; {batch.biomass_input_kg}kg input
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {batch.char_output_kg && (
                        <span className="text-sm font-medium text-green-600">
                          {batch.char_output_kg}kg output
                        </span>
                      )}
                      <span className={clsx('px-2.5 py-1 text-xs font-medium rounded-full', config.color)}>
                        {config.label}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-earth-500">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p className="font-medium">No batches yet</p>
              <Link to="/biochar/new" className="btn btn-primary mt-3 inline-flex">
                <Plus className="h-4 w-4 mr-2" />
                Create First Batch
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
